import axios from 'axios';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import { DataTable, EmptyState, LoadingButton, Notice, PageHeader, StatusChip, type StatusTone } from '@routes-plan/ui';
import PermissionButton from '../../../components/common/PermissionButton';
import StandardModal from '../../../components/modales/StandardModal';
import { AdminPage, ContentCard, useAlert, useConfirm } from '../shared/AdminUi';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { setPageTitle } from '../../../utils/pageTitle';
import { operationalTimezone } from '../../../utils/operationalDateTime';
import { useT, type TextKey } from '../../../i18n';

/**
 * Administrar → Dispositivos: aprobar o bloquear los teléfonos de los representantes, cerrar su sesión,
 * pedir el borrado remoto de los datos y exigir equipo aprobado en todos los cobros.
 * Cada usuario ve solo los cobros de sus oficinas (el API filtra).
 */
interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

type Status = 'pending' | 'approved' | 'blocked';
type Filter = Status | 'all';
type Action = 'approve' | 'block' | 'revoke' | 'wipe';

interface Device {
  id: number;
  status: Status;
  deviceName: string | null;
  platform: string | null;
  brand: string | null;
  model: string | null;
  appVersion: string | null;
  lastLoginAt: string | null;
  lastSeenAt: string | null;
  approvedAt: string | null;
  blockedAt: string | null;
  requestedAt: string | null;
  reviewNote: string | null;
  wipeRequestedAt: string | null;
  requestReason: string | null;
  salesRep: { id: number; username: string; name: string };
  route: { id: number; name: string; deviceRequired: boolean };
  office: { id: number; name: string } | null;
}

interface Policy {
  totalRoutes: number;
  requiredRoutes: number;
  allRequired: boolean;
}

interface HistoryRow {
  id: number;
  action: string;
  note: string | null;
  at: string;
  actor: string | null;
}

const STATUS_TONE: Record<Status, StatusTone> = { pending: 'warning', approved: 'success', blocked: 'neutral' };

export const formatDateTime = (value: string | null) =>
  value ? moment(value).tz(operationalTimezone()).format('YYYY-MM-DD HH:mm') : '—';

const DeviceList: React.FC<ShowAlerts> = () => {
  setPageTitle('menu.admin.devices');
  const t = useT();
  const showAlert = useAlert();
  const [confirmAsk, confirmDialog] = useConfirm();
  const [loading, setLoading] = useState(true);
  const { canRead, canUpdate } = useResourcePermissions('/admin/devices');
  const [filter, setFilter] = useState<Filter>('pending');
  const [devices, setDevices] = useState<Device[]>([]);
  const [summary, setSummary] = useState<Record<Status, number>>({ pending: 0, approved: 0, blocked: 0 });
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [dialog, setDialog] = useState<{ action: Action; device: Device } | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; device: Device } | null>(null);
  const [history, setHistory] = useState<{ device: Device; rows: HistoryRow[] } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, pol] = await Promise.all([
        axios.get<{ data: Device[]; summary: Record<Status, number> }>('devices', { params: filter === 'all' ? {} : { status: filter } }),
        axios.get<Policy>('devices/policy'),
      ]);
      setDevices(list.data.data);
      setSummary(list.data.summary ?? { pending: 0, approved: 0, blocked: 0 });
      setPolicy(pol.data);
    } catch {
      showAlert(t('devices.loadError'), 'error');
    } finally {
      setLoaded(true);
      setLoading(false);
    }
  }, [filter, showAlert, t]);

  useEffect(() => {
    if (canRead) void load();
  }, [canRead, load]);

  const openDialog = (action: Action, device: Device) => {
    setMenu(null);
    setNote('');
    setDialog({ action, device });
  };

  const confirm = async () => {
    if (!dialog) return;
    const { action, device } = dialog;
    setSaving(true);
    try {
      if (action === 'approve' || action === 'block') {
        const { data } = await axios.put<{ replaced: number[] }>(`devices/${device.id}/status`, { action, note });
        showAlert(
          action === 'approve'
            ? data.replaced?.length
              ? t('devices.approvedReplaced', { count: data.replaced.length })
              : t('devices.approvedOk')
            : t(device.status === 'pending' ? 'devices.rejectedOk' : 'devices.blockedOk'),
          'success',
        );
      } else if (action === 'revoke') {
        await axios.post(`devices/${device.id}/revoke-session`, { note });
        showAlert(t('devices.revokedOk'), 'success');
      } else {
        await axios.post(`devices/${device.id}/wipe`, { note });
        showAlert(t('devices.wipeOk'), 'success');
      }
      setDialog(null);
      await load();
    } catch {
      showAlert(t('devices.actionError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const togglePolicy = async (required: boolean) => {
    if (!canUpdate) return;
    if (!required && !(await confirmAsk({ title: t('devices.policyOffTitle'), message: t('devices.policyOffConfirm'), variant: 'warning', confirmLabel: t('devices.policyOffAction') }))) return;
    setLoading(true);
    try {
      const { data } = await axios.put<Policy>('devices/policy', { required });
      setPolicy(data);
      showAlert(required ? t('devices.policyOnOk') : t('devices.policyOffOk'), 'success');
      await load();
    } catch {
      showAlert(t('devices.actionError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const openHistory = async (device: Device) => {
    setMenu(null);
    try {
      const { data } = await axios.get<{ data: HistoryRow[] }>(`devices/${device.id}/history`);
      setHistory({ device, rows: data.data });
    } catch {
      showAlert(t('devices.loadError'), 'error');
    }
  };

  const phoneOf = (d: Device) =>
    [[d.brand, d.model].filter(Boolean).join(' ') || d.deviceName || t('devices.unknownDevice'), d.platform].filter(Boolean).join(' · ');

  const dialogText: Record<Action, { title: TextKey; body: TextKey; confirm: TextKey }> = {
    approve: { title: 'devices.dialog.approveTitle', body: 'devices.dialog.approveBody', confirm: 'devices.approve' },
    block: { title: 'devices.dialog.blockTitle', body: 'devices.dialog.blockBody', confirm: 'devices.block' },
    revoke: { title: 'devices.dialog.revokeTitle', body: 'devices.dialog.revokeBody', confirm: 'devices.revoke' },
    wipe: { title: 'devices.dialog.wipeTitle', body: 'devices.dialog.wipeBody', confirm: 'devices.wipe' },
  };

  // Un teléfono pendiente se "rechaza" (mismo bloqueo en el API, con su propio texto).
  const textsFor = (d: { action: Action; device: Device }) =>
    d.action === 'block' && d.device.status === 'pending'
      ? { title: 'devices.dialog.rejectTitle' as TextKey, body: 'devices.dialog.rejectBody' as TextKey, confirm: 'devices.reject' as TextKey }
      : dialogText[d.action];

  if (!canRead && loaded) {
    return (
      <AdminPage>
        <PageHeader title={t('devices.title')} />
        <Notice severity="warning">{t('devices.noReadPermission')}</Notice>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <PageHeader
        title={t('devices.title')}
        description={t('devices.subtitle')}
        actions={
          <Button variant="outlined" color="inherit" startIcon={<RefreshIcon />} onClick={() => void load()}>
            {t('common.refresh')}
          </Button>
        }
      />
      <Notice severity="info" dismissible>
        {t('devices.intro')}
      </Notice>
      {policy && (
        <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('devices.policyLabel')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('devices.policyStatus', { required: policy.requiredRoutes, total: policy.totalRoutes })}
            </Typography>
          </Box>
          <FormControlLabel
            data-tour="devices-policy"
            control={<Switch checked={policy.allRequired} disabled={!canUpdate || policy.totalRoutes === 0} onChange={(e) => void togglePolicy(e.target.checked)} />}
            label={policy.allRequired ? t('common.enabled') : t('common.disabled')}
          />
        </Paper>
      )}
      <ContentCard>
        <Tabs
          data-tour="devices-tabs"
          value={filter}
          onChange={(_, v) => setFilter(v)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab value="pending" label={`${t('devices.tabPending')} (${summary.pending})`} />
          <Tab value="approved" label={`${t('devices.tabApproved')} (${summary.approved})`} />
          <Tab value="blocked" label={`${t('devices.tabBlocked')} (${summary.blocked})`} />
          <Tab value="all" label={t('devices.tabAll')} />
        </Tabs>
        <DataTable<Device>
          rows={devices}
          loading={loading && !loaded}
          getRowId={(d) => d.id}
          ariaLabel={t('devices.title')}
          searchable
          searchPlaceholder={t('devices.searchPlaceholder')}
          exportFileName={t('devices.exportFileName')}
          columns={[
            {
              id: 'salesRep',
              header: t('devices.colSalesRep'),
              value: (d) => d.salesRep.name || d.salesRep.username,
              render: (d) => (
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                    {d.salesRep.name || d.salesRep.username}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap component="div">
                    {d.salesRep.username}
                  </Typography>
                </Box>
              ),
              sortable: true,
            },
            {
              id: 'status',
              header: t('devices.colStatus'),
              value: (d) => t(`devices.status.${d.status}` as TextKey),
              render: (d) => (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <StatusChip label={t(`devices.status.${d.status}` as TextKey)} tone={STATUS_TONE[d.status]} />
                  {d.wipeRequestedAt && <StatusChip label={t('devices.wipePending')} tone="danger" />}
                </Box>
              ),
              sortable: true,
            },
            { id: 'phone', header: t('devices.colDevice'), value: (d) => phoneOf(d), hideBelow: 'sm' },
            {
              id: 'route',
              header: t('devices.colRoute'),
              value: (d) => d.route.name + (d.route.deviceRequired ? '' : ` (${t('devices.notRequired')})`),
              sortable: true,
            },
            { id: 'office', header: t('devices.colOffice'), value: (d) => d.office?.name ?? '—', sortable: true, hideBelow: 'md' },
            { id: 'reason', header: t('devices.colNote'), value: (d) => d.requestReason || d.reviewNote || '', wrap: true, width: 220, hideBelow: 'md' },
            { id: 'appVersion', header: t('devices.colAppVersion'), value: (d) => d.appVersion ?? '—', hideBelow: 'md' },
            { id: 'lastSeen', header: t('devices.colLastSeen'), value: (d) => d.lastSeenAt ?? '', render: (d) => formatDateTime(d.lastSeenAt), sortable: true, hideBelow: 'sm' },
            { id: 'requested', header: t('devices.colRequested'), value: (d) => d.requestedAt ?? '', render: (d) => formatDateTime(d.requestedAt), sortable: true, hideBelow: 'md' },
          ]}
          rowActions={(d) => (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {d.status !== 'approved' && (
                <PermissionButton
                  allowed={canUpdate}
                  deniedTooltip={t('devices.noPermission')}
                  tooltip={t('devices.approve')}
                  data-tour="device-approve"
                  variant="outlined"
                  color="success"
                  size="small"
                  sx={{ minWidth: 34, px: 0.75 }}
                  onClick={() => openDialog('approve', d)}
                  aria-label={`${t('devices.approve')} ${d.salesRep.username}`}
                >
                  <CheckIcon fontSize="small" />
                </PermissionButton>
              )}
              {d.status !== 'blocked' && (
                <PermissionButton
                  allowed={canUpdate}
                  deniedTooltip={t('devices.noPermission')}
                  tooltip={d.status === 'pending' ? t('devices.reject') : t('devices.block')}
                  variant="outlined"
                  color="error"
                  size="small"
                  sx={{ minWidth: 34, px: 0.75 }}
                  onClick={() => openDialog('block', d)}
                  aria-label={`${d.status === 'pending' ? t('devices.reject') : t('devices.block')} ${d.salesRep.username}`}
                >
                  <BlockIcon fontSize="small" />
                </PermissionButton>
              )}
              <Tooltip title={t('devices.more')}>
                <IconButton size="small" aria-label={`${t('devices.more')} ${d.salesRep.username}`} onClick={(e) => setMenu({ anchor: e.currentTarget, device: d })}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
          emptyState={
            <EmptyState
              icon={<PhoneAndroidOutlinedIcon />}
              title={filter === 'pending' ? t('devices.emptyPending') : t('devices.empty')}
              dense
            />
          }
        />
      </ContentCard>

      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => menu && void openHistory(menu.device)}>{t('devices.history')}</MenuItem>
        <MenuItem disabled={!canUpdate} onClick={() => menu && openDialog('revoke', menu.device)}>
          {t('devices.revoke')}
        </MenuItem>
        <MenuItem disabled={!canUpdate} onClick={() => menu && openDialog('wipe', menu.device)} sx={{ color: 'error.main' }}>
          {t('devices.wipe')}
        </MenuItem>
      </Menu>

      {dialog && (
        <StandardModal
          open
          onClose={() => !saving && setDialog(null)}
          title={t(textsFor(dialog).title)}
          subtitle={`${dialog.device.salesRep.name || dialog.device.salesRep.username} · ${dialog.device.route.name}`}
          maxWidth={520}
          disableClose={saving}
          actions={
            <>
              <Button variant="outlined" color="inherit" onClick={() => setDialog(null)} disabled={saving}>
                {t('common.cancel')}
              </Button>
              <LoadingButton
                variant="contained"
                color={dialog.action === 'approve' ? 'success' : dialog.action === 'revoke' ? 'primary' : 'error'}
                onClick={() => void confirm()}
                loading={saving}
              >
                {t(textsFor(dialog).confirm)}
              </LoadingButton>
            </>
          }
        >
          <Stack spacing={2}>
            <Typography variant="body2">{t(textsFor(dialog).body)}</Typography>
            {dialog.action === 'wipe' && <Notice severity="warning">{t('devices.dialog.wipeWarning')}</Notice>}
            <TextField
              label={t('devices.dialog.note')}
              helperText={t('devices.dialog.noteHint')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              inputProps={{ maxLength: 300 }}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </StandardModal>
      )}

      {history && (
        <StandardModal open onClose={() => setHistory(null)} title={t('devices.history')} subtitle={history.device.salesRep.name || history.device.salesRep.username} maxWidth={560}>
          {history.rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('devices.historyEmpty')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {history.rows.map((h) => (
                <ListItem key={h.id} divider disableGutters>
                  <ListItemText
                    primary={t(`devices.audit.${h.action}` as TextKey)}
                    secondary={[formatDateTime(h.at), h.actor ?? t('devices.byApp'), h.note].filter(Boolean).join(' · ')}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </StandardModal>
      )}
      {confirmDialog}
    </AdminPage>
  );
};

export default DeviceList;
